import React, { ChangeEvent, useId, useState } from 'react';
import Image from 'next/legacy/image';

import {
  TextInputWrapper,
  InputLabel,
  Input,
  ErrorMessage,
  IconWrapper,
  TogglePasswordBtn
} from './style';

import { BaseInputProps, TextInputType, TextInputWidth } from '../shared';

import icon from '@/images/error_icon.svg';

interface TextInputProps extends BaseInputProps {
  borderColor?: string;
  handleOnChange: (event: ChangeEvent<HTMLInputElement>, value: any) => void;
}

export function TextInput({
  value,
  type,
  label,
  placeholder,
  error,
  required,
  handleOnChange,
  disabled = false,
  width = TextInputWidth.FULL,
  borderColor = '#00D9B4',
  inputMode,
  pattern,
  minLength,
  maxLength,
  autoComplete
}: TextInputProps) {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const inputId = useId();
  const [showPassword, setShowPassword] = useState<boolean>(false);

  return (
    <>
      <TextInputWrapper width={width} error={!!error} borderColor={borderColor}>
        <InputLabel htmlFor={inputId} floatLabel={!!value || isFocused}>
          {label}
          {required ? '*' : ''}
        </InputLabel>
        <Input
          id={inputId}
          type={showPassword ? TextInputType.TEXT : type}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          floatLabel={!!value || isFocused}
          inputMode={inputMode}
          pattern={pattern}
          autoComplete={autoComplete ?? 'off'}
          min="0"
          minLength={minLength || 1}
          maxLength={maxLength || 320}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            handleOnChange?.(e, e.target.value);
          }}
          onFocus={() => {
            setIsFocused(true);
          }}
          onBlur={() => {
            setIsFocused(false);
          }}
          aria-required={required}
          aria-invalid={!!error}
          data-testid={`input-${type}-${label}`}
        />
        {type === TextInputType.PASSWORD && (
          <TogglePasswordBtn
            onClick={() => setShowPassword(!showPassword)}
            active={showPassword}
            data-testid={`input-${type}-${label}-btn-${showPassword}`}
          />
        )}
      </TextInputWrapper>
      <ErrorMessage
        data-testid={`input-${type}-${label}-error`}
        visible={!!error}
      >
        <IconWrapper>
          <Image src={icon} alt="Form input error" />
        </IconWrapper>
        {error || ''}
      </ErrorMessage>
    </>
  );
}
